import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enLocale from './locales/en.json';
import ruLocale from './locales/ru.json';
import uaLocale from './locales/ua.json';

type AppLanguage = 'en' | 'ru' | 'ua';

const resources = {
  en: { translation: enLocale },
  ru: { translation: ruLocale },
  ua: { translation: uaLocale },
};

const normalizeLanguage = (value: string | null | undefined): AppLanguage => {
  const normalized = String(value ?? '').toLowerCase();
  if (normalized.startsWith('ru')) return 'ru';
  if (normalized.startsWith('ua') || normalized.startsWith('uk')) return 'ua';
  return 'en';
};

const getCookieValue = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`, 'i'));
  return match ? decodeURIComponent(match[1]) : null;
};

const setLanguageCookie = (language: AppLanguage): void => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  const host = window.location.hostname;
  const isBitBotHubDomain = host.endsWith('bitbothub.com');
  const parts = [
    `lang=${encodeURIComponent(language)}`,
    'Path=/',
    'Max-Age=31536000',
    'SameSite=Lax',
  ];
  if (isBitBotHubDomain) {
    parts.push('Domain=.bitbothub.com');
  }
  if (isBitBotHubDomain || window.location.protocol === 'https:') {
    parts.push('Secure');
  }
  document.cookie = parts.join('; ');
};

const getSavedLanguage = (): AppLanguage => {
  const cookieLanguage = getCookieValue('lang');
  if (cookieLanguage) {
    return normalizeLanguage(cookieLanguage);
  }

  try {
    return normalizeLanguage(localStorage.getItem('language'));
  } catch {
    return 'en';
  }
};

const savedLanguage = getSavedLanguage();

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    lng: savedLanguage,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });

i18n.on('languageChanged', (language) => {
  const normalized = normalizeLanguage(language);

  try {
    localStorage.setItem('language', normalized);
  } catch {
    // ignored
  }

  setLanguageCookie(normalized);
});

export default i18n;
